import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox } from '@roger/ui-components';

import { Check, Edit, Loader2, RotateCcw } from 'lucide-react';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { getPayrollSettings, updatePayrollSettings } from '../../services/api';
import type {
  PayrollSettingsResponse,
  TaskStatus,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { payrollStatusLabel } from '../payroll/payrollStatusLabels';

const HINT_ID = 'payroll-statuses-hint';

/**
 * Which visit statuses reach Payroll Manager: the Unpaid list, its export and
 * Mark as Paid all take the agency's set from here.
 *
 * The server owns every list on this card — the statuses offered, Roger's
 * default and the agency's current set — so a status added or retired there
 * needs no change here. Only the labels are local, from payroll's one label
 * map.
 */
const PayrollSettingsSection: React.FC = () => {
  const { canEdit } = useAdminSectionAccess('payroll');
  const { agencyId, showSuccess, showError } = useStore(
    useShallow(state => ({
      agencyId: state.user?.agency?.id,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isOpeningEditor, setIsOpeningEditor] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // A failed read says nothing about this agency's set, so the card must not
  // offer to replace it: a save from here would overwrite a setting nobody
  // was shown.
  const [loadFailed, setLoadFailed] = useState(false);
  // Last known server state. Edits work on `ticked` and only reach this once
  // the server has accepted them.
  const [settings, setSettings] = useState<PayrollSettingsResponse | null>(
    null
  );
  const [ticked, setTicked] = useState<ReadonlySet<TaskStatus>>(new Set());
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  // Guard Edit's fresh read and Save: one of each at a time, and nothing from
  // either lands (a toast on the next page, say) once the card has gone.
  const opening = useRef(false);
  const saving = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!agencyId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const response = await getPayrollSettings({ throwOnError: true });
        if (!cancelled) setSettings(response.data);
      } catch (err) {
        console.error('Failed to load payroll settings:', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  // The editor starts from the set as stored now, not as this card loaded it:
  // Save replaces the whole set, so ticks taken from a stale card would quietly
  // undo another admin's newer save.
  const startEditing = async () => {
    if (opening.current) return;
    opening.current = true;
    setIsOpeningEditor(true);
    try {
      const response = await getPayrollSettings({ throwOnError: true });
      if (!mounted.current) return;
      setSettings(response.data);
      setTicked(new Set(response.data.eligible_statuses));
      setIsEditing(true);
    } catch (err) {
      console.error('Failed to load payroll settings:', err);
      if (mounted.current) showError("Couldn't load payroll settings");
    } finally {
      opening.current = false;
      if (mounted.current) setIsOpeningEditor(false);
    }
  };

  const toggle = (status: TaskStatus, checked: boolean) => {
    setTicked(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(status);
      } else {
        next.delete(status);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!settings || ticked.size === 0 || saving.current) return;
    // In the order the statuses are offered, which is the order they are
    // stored and listed back. Sent even when unchanged, so the admin's Save
    // always stores what they see; the editor opened on a fresh read.
    const selected = settings.selectable_statuses
      .map(option => option.value)
      .filter(status => ticked.has(status));

    saving.current = true;
    setIsSaving(true);
    try {
      const response = await updatePayrollSettings({
        body: { eligible_statuses: selected },
        throwOnError: true,
      });
      if (!mounted.current) return;
      setSettings(response.data);
      setIsEditing(false);
      showSuccess('Payroll settings updated');
    } catch (err) {
      // The selection stays as the admin left it, so a refused save can be
      // fixed and retried rather than redone from scratch.
      console.error('Failed to update payroll settings:', err);
      if (mounted.current) {
        showError(
          getBusinessErrorMessage(err) ?? "Couldn't save payroll settings"
        );
      }
    } finally {
      saving.current = false;
      if (mounted.current) setIsSaving(false);
    }
  };

  const editing = isEditing && canEdit && settings !== null;
  const nothingTicked = ticked.size === 0;

  // Edit and the editor's controls replace each other, so focus would fall to
  // the page and a keyboard user would lose their place. Move it into the
  // editor on the way in, and back to Edit on the way out.
  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing) {
      fieldsetRef.current
        ?.querySelector<HTMLElement>('[role="checkbox"]')
        ?.focus();
    } else if (wasEditing.current) {
      editButtonRef.current?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  return (
    <div className='space-y-6'>
      <div className='space-y-1'>
        <p className='text-sm text-muted-foreground'>
          Visits in these statuses appear in Payroll Manager&apos;s Unpaid list
          and can be marked paid. Visits already in a payroll are not affected.
          Missed visits and task types marked non-billable never appear.
        </p>
        {loadFailed && (
          <p className='text-xs text-destructive'>
            These settings did not load. Reload the page to see them.
          </p>
        )}
      </div>

      {isLoading ? (
        <Loader2
          className='h-4 w-4 animate-spin text-muted-foreground'
          aria-label='Loading payroll settings'
        />
      ) : (
        settings && (
          <fieldset ref={fieldsetRef} className='space-y-2'>
            <legend className='sr-only'>
              Statuses that reach Payroll Manager
            </legend>
            <div className='grid gap-2 sm:grid-cols-2'>
              {settings.selectable_statuses.map(({ value }) => (
                <label
                  key={value}
                  className='flex items-center gap-2 rounded-md border p-3 text-sm'
                >
                  <Checkbox
                    checked={
                      editing
                        ? ticked.has(value)
                        : settings.eligible_statuses.includes(value)
                    }
                    disabled={!editing || isSaving}
                    onCheckedChange={checked => toggle(value, checked === true)}
                  />
                  {payrollStatusLabel(value)}
                </label>
              ))}
            </div>
            {editing && nothingTicked && (
              <p id={HINT_ID} className='text-xs text-destructive'>
                Select at least one status.
              </p>
            )}
          </fieldset>
        )
      )}

      <div className='flex flex-wrap items-center justify-end gap-2'>
        {editing ? (
          <>
            {/* Keyed apart from Edit: otherwise React reuses Edit's button,
                focus and all, as Reset, and an Enter would reset the set. */}
            <Button
              key='reset'
              variant='ghost'
              onClick={() => setTicked(new Set(settings.default_statuses))}
              disabled={isSaving}
              className='mr-auto'
            >
              <RotateCcw className='mr-2 h-4 w-4' />
              Reset to Roger default
            </Button>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                // Busy, not disabled, while saving: disabling the focused
                // button would drop focus to the page, and a refused save
                // keeps the editor open for another try.
                disabled={nothingTicked}
                aria-busy={isSaving}
                aria-describedby={nothingTicked ? HINT_ID : undefined}
              >
                {isSaving ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className='mr-2 h-4 w-4' />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </>
        ) : (
          canEdit && (
            <Button
              key='edit'
              ref={editButtonRef}
              variant='outline'
              onClick={() => void startEditing()}
              disabled={isLoading || loadFailed}
              aria-busy={isOpeningEditor}
            >
              {isOpeningEditor ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : (
                <Edit className='mr-2 h-4 w-4' />
              )}
              Edit
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default PayrollSettingsSection;
